import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed. The link may be expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6">
        <div className="glass rounded-2xl p-8 max-w-md text-center space-y-4">
          <h1 className="font-heading text-2xl text-white">Invalid link</h1>
          <p className="text-slate-400 text-sm">This password reset link is missing or invalid.</p>
          <Link to="/forgot-password">
            <Button className="bg-cyan text-navy-950 hover:bg-cyan-300" data-testid="invalid-request-link">Request a new link</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(0,212,255,0.1)_0%,transparent_60%)]" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative">
        <Link to="/" className="flex justify-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_7d9bc2e5-ff38-4e06-b101-9f0eb41c2616/artifacts/w350as9h_LOGOvrn-02.png"
            alt="VerbNotes"
            className="h-16"
          />
        </Link>

        <div className="glass rounded-2xl p-8">
          {done ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center">
                <CheckCircle className="text-emerald-400" size={28} />
              </div>
              <h1 className="font-heading text-2xl text-white">Password updated</h1>
              <p className="text-slate-400 text-sm">Your password has been reset. You can now log in with your new password.</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-cyan text-navy-950 hover:bg-cyan-300 rounded-full px-8 py-5 font-semibold"
                data-testid="reset-go-login-btn"
              >
                Go to login
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-3xl text-white text-center mb-2">New password</h1>
              <p className="text-slate-400 text-center mb-6 text-sm">Choose a new password for your account.</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">New password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500 pr-10"
                      autoFocus
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-slate-300">Confirm password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    placeholder="Repeat your new password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500"
                    data-testid="reset-confirm-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-cyan text-navy-950 hover:bg-cyan-300 rounded-full py-6 font-semibold shadow-[0_0_20px_rgba(0,212,255,0.3)]"
                  data-testid="reset-submit-btn"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : 'Reset password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
